import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ShoppingBag, Store, Eye, EyeOff, Mail, Lock, ArrowRight, X, KeyRound, Check, Smartphone } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_live-commerce-hub-4/artifacts/29xleaxd_ChatGPT%20Image%20May%2014%2C%202026%2C%2012_57_41%20PM.png";

// Demo-account autofill is opt-in via env var for preview/QA builds only.
// In production REACT_APP_ENABLE_DEMO_LOGIN is unset/false, so the form
// starts empty. Demo passwords are NEVER hardcoded here — they only apply
// when explicitly provided via REACT_APP_DEMO_*_PW env vars, so no
// credential can ever leak into the shipped bundle by default.
const DEMO_LOGIN_ENABLED = process.env.REACT_APP_ENABLE_DEMO_LOGIN === "true";

// ISO country dial codes (top markets first, then alphabetical)
const COUNTRY_CODES = [
  ["US", "+1",   "United States"],
  ["IN", "+91",  "India"],
  ["GB", "+44",  "United Kingdom"],
  ["CA", "+1",   "Canada"],
  ["AU", "+61",  "Australia"],
  ["AE", "+971", "UAE"],
  ["SG", "+65",  "Singapore"],
  ["DE", "+49",  "Germany"],
  ["FR", "+33",  "France"],
  ["IT", "+39",  "Italy"],
  ["ES", "+34",  "Spain"],
  ["NL", "+31",  "Netherlands"],
  ["JP", "+81",  "Japan"],
  ["KR", "+82",  "South Korea"],
  ["CN", "+86",  "China"],
  ["MX", "+52",  "Mexico"],
  ["BR", "+55",  "Brazil"],
  ["NG", "+234", "Nigeria"],
  ["ZA", "+27",  "South Africa"],
  ["SA", "+966", "Saudi Arabia"],
];

const PRESETS = {
  buyer: {
    label: "Buyer",
    tagline: "Shop live drops & snag deals",
    icon: ShoppingBag,
    color: "#D4FF00",
    email: DEMO_LOGIN_ENABLED ? "buyer@shoprizz.com" : "",
    password: DEMO_LOGIN_ENABLED ? (process.env.REACT_APP_DEMO_BUYER_PW || "") : "",
  },
  seller: {
    label: "Seller",
    tagline: "Go live. Sell global.",
    icon: Store,
    color: "#FF007F",
    email: DEMO_LOGIN_ENABLED ? "seller@shoprizz.com" : "",
    password: DEMO_LOGIN_ENABLED ? (process.env.REACT_APP_DEMO_SELLER_PW || "") : "",
  },
  // NOTE: admins sign in via the dedicated portal at /admin-login —
  // the consumer login page intentionally exposes buyer + seller only.
};

export default function LoginPage() {
  const { login, logout, googleLogin, otpSend, otpVerify } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("buyer");
  const [email, setEmail] = useState(PRESETS.buyer.email);
  const [password, setPassword] = useState(PRESETS.buyer.password);
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [adminGate, setAdminGate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [remember, setRemember] = useState(true);
  // Mobile login state
  const [method, setMethod] = useState("email");
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState("phone");   // phone | code
  const [otpInfo, setOtpInfo] = useState(null);

  // IP-geo: auto-default the country dropdown on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("https://ipapi.co/json/");
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        const iso = (j.country_code || "").toUpperCase();
        const callingCode = j.country_calling_code; // e.g. "+91"
        if (callingCode && COUNTRY_CODES.some(([i, c]) => c === callingCode)) {
          setCountryCode(callingCode);
        } else if (iso) {
          // Match by ISO if calling code missing
          const hit = COUNTRY_CODES.find(([i]) => i === iso);
          if (hit) setCountryCode(hit[1]);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const pickRole = (r) => {
    setRole(r);
    setEmail(PRESETS[r].email);
    setPassword(PRESETS[r].password);
    setErr("");
    setAdminGate(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setAdminGate(false); setBusy(true);
    try {
      const u = await login(email, password, remember);
      // Admins must use the dedicated /admin-login portal — tear down
      // the session so a consumer-page login never leaks an admin token.
      if (u.role === "admin") {
        await logout();
        setAdminGate(true);
        return;
      }
      if (u.role === "seller") nav("/seller");
      else nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    setErr("");
    const e164 = `${countryCode}${(phone || "").replace(/\D/g, "")}`;
    if (e164.replace(/\D/g, "").length < 8) {
      setErr("Please enter a valid mobile number.");
      return;
    }
    setBusy(true);
    try {
      const data = await otpSend(e164);
      setOtpInfo(data);
      setOtpStep("code");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Failed to send OTP.");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setErr(""); setAdminGate(false);
    const e164 = `${countryCode}${(phone || "").replace(/\D/g, "")}`;
    setBusy(true);
    try {
      const u = await otpVerify(e164, otp, "", remember);
      if (u.role === "admin") {
        await logout();
        setAdminGate(true);
        return;
      }
      if (u.role === "seller") nav("/seller");
      else nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Invalid code.");
    } finally { setBusy(false); }
  };

  const R = PRESETS[role];      // eslint-disable-line no-unused-vars


  return (
    <div className="min-h-[calc(100vh-80px)] px-4 py-6 max-w-md mx-auto flex flex-col">
      <div className="mb-4">
        <Link to="/" className="text-xs font-bold uppercase tracking-widest text-[#A1A1AA] hover:text-[#D4FF00]" data-testid="login-back-btn">← Back</Link>
      </div>

      {/* Splash logo + tagline */}
      <div className="flex flex-col items-center text-center mt-6 mb-7">
        <img src={LOGO} alt="ShopRizz" className="h-20 sm:h-24 w-auto mb-5 drop-shadow-[0_0_30px_rgba(212,255,0,0.25)]"/>
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase leading-[0.95]">
          shop the <span className="title-gradient">feed</span>.
        </h1>
        <p className="text-[#A1A1AA] text-sm mt-3 max-w-xs">Live drops, real sellers, curated for you.</p>
      </div>

      {/* Auth card */}
      <div className="card p-5 grain relative overflow-hidden">
        {/* Role chooser (Buyer / Seller — admins use /admin-login) */}
        <div className="grid grid-cols-2 gap-1.5 mb-4 p-1 bg-[#1A0F1F] rounded-full border border-[#3D2845]" data-testid="login-role-tabs">
          {Object.entries(PRESETS).map(([key, p]) => {
            const I = p.icon;
            const active = role === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => pickRole(key)}
                data-testid={`login-role-${key}`}
                aria-pressed={active}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${
                  active ? "shadow-md" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
                }`}
                style={active ? { background: p.color, color: "#000" } : {}}
              >
                <I size={12} strokeWidth={2.8}/>
                {p.label}
              </button>
            );
          })}
        </div>

        <button onClick={googleLogin} data-testid="google-login-btn"
          className="w-full mb-4 py-3 rounded-full bg-white text-black font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition shadow-md">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9C29 35.7 26.6 36.5 24 36.5c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.7 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6 4.9C41.4 34.9 44 29.8 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
          Continue with Google
        </button>

        {/* Method toggle: email vs mobile */}
        <div className="flex items-center mb-4 gap-1 p-1 bg-[#1A0F1F] rounded-full border border-[#3D2845]" data-testid="login-method-tabs">
          <button type="button" onClick={() => setMethod("email")}
            data-testid="login-method-email"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${
              method === "email" ? "bg-[#FAFAFA] text-black" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
            }`}>
            <Mail size={12}/> Email
          </button>
          <button type="button" onClick={() => setMethod("mobile")}
            data-testid="login-method-mobile"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${
              method === "mobile" ? "bg-[#FAFAFA] text-black" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
            }`}>
            <Smartphone size={12}/> Mobile
          </button>
        </div>

        {method === "email" && (
          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-email-form">
            <div className="relative">
              <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]"/>
              <input data-testid="login-email" type="email" required placeholder="Email" className="input-dark pl-10" value={email} onChange={e=>setEmail(e.target.value)}/>
            </div>
            <div className="relative">
              <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]"/>
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                required
                placeholder="Password"
                className="input-dark pl-10 pr-12"
                value={password}
                onChange={e=>setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={()=>setShowPw(!showPw)}
                data-testid="login-toggle-pw"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#D4FF00] p-1"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
            <div className="flex items-center justify-between -mt-1">
              <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer select-none">
                <input type="checkbox" checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="remember-me-checkbox"
                  className="accent-[#D4FF00]"/>
                Remember me
              </label>
              <button
                type="button"
                onClick={()=>setForgotOpen(true)}
                data-testid="forgot-pw-btn"
                className="text-xs text-[#A1A1AA] hover:text-[#D4FF00]"
              >
                Forgot password?
              </button>
            </div>
            {err && <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
            {adminGate && (
              <div className="text-sm text-[#00F0FF] bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg px-3 py-2"
                data-testid="admin-gate-notice">
                Admin accounts must sign in via the dedicated admin portal.
              </div>
            )}
            <button type="submit" data-testid="login-submit-btn" disabled={busy} className="btn-primary w-full">
              {busy ? "Signing in…" : `Log in as ${PRESETS[role].label}`}
            </button>
          </form>
        )}

        {method === "mobile" && (
          <div className="space-y-3" data-testid="login-mobile-form">
            {otpStep === "phone" && (
              <>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    data-testid="login-country-code"
                    aria-label="Country code"
                    className="input-dark px-2 py-2 text-sm shrink-0 cursor-pointer"
                    style={{ width: "108px" }}
                  >
                    {COUNTRY_CODES.map(([iso, code, name]) => (
                      <option key={`${iso}${code}`} value={code}>{iso} {code}</option>
                    ))}
                  </select>
                  <div className="relative flex-1 min-w-0">
                    <Smartphone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]"/>
                    <input type="tel" inputMode="numeric"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^\d ]/g, ""))}
                      placeholder="Mobile number"
                      data-testid="login-phone"
                      className="input-dark pl-10 w-full"/>
                  </div>
                </div>
                {err && <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2">{err}</div>}
                <button type="button" onClick={sendOtp} disabled={busy || !phone.trim()}
                  data-testid="login-otp-send"
                  className="btn-primary w-full disabled:opacity-50">
                  {busy ? "Sending…" : "Send OTP"}
                </button>
                <p className="text-[10px] text-center text-[#52525B] -mt-1">
                  We'll send a 6-digit code to verify your number.
                </p>
              </>
            )}
            {otpStep === "code" && (
              <>
                <div className="text-xs text-[#A1A1AA] bg-[#1A0F1F] border border-[#3D2845] rounded-lg p-3">
                  Code sent to <span className="text-[#FAFAFA] font-bold">{countryCode} {phone}</span>{" "}
                  <button type="button" onClick={() => { setOtpStep("phone"); setOtp(""); setErr(""); }}
                    className="text-[#D4FF00] underline ml-1" data-testid="login-otp-edit-phone">Change</button>
                </div>
                {otpInfo?.mock && otpInfo?.hint && (
                  <div className="text-[10px] text-[#D4FF00] bg-[#D4FF00]/5 border border-[#D4FF00]/20 rounded-lg px-3 py-2" data-testid="login-otp-mock-hint">
                    {otpInfo.hint}
                  </div>
                )}
                <div className="relative">
                  <KeyRound size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]"/>
                  <input type="text" inputMode="numeric" maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    data-testid="login-otp"
                    autoFocus
                    className="input-dark pl-10 w-full tracking-[0.5em] text-center font-mono-display"/>
                </div>
                <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer select-none">
                  <input type="checkbox" checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="accent-[#D4FF00]"/>
                  Remember me
                </label>
                {err && <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2">{err}</div>}
                {adminGate && (
                  <div className="text-sm text-[#00F0FF] bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg px-3 py-2"
                    data-testid="admin-gate-notice">
                    Admin accounts must sign in via the dedicated admin portal.
                  </div>
                )}
                <button type="button" onClick={verifyOtp} disabled={busy || otp.length < 4}
                  data-testid="login-otp-verify"
                  className="btn-primary w-full disabled:opacity-50">
                  {busy ? "Verifying…" : "Verify & continue"}
                </button>
                <button type="button" onClick={sendOtp} disabled={busy}
                  data-testid="login-otp-resend"
                  className="text-xs text-[#A1A1AA] hover:text-[#D4FF00] block mx-auto">
                  Resend code
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        Don't have an account? <Link to="/register" data-testid="go-register-link" className="text-[#D4FF00] font-bold">Sign up</Link>
      </p>
      <p className="text-center text-xs text-[#52525B] mt-2">
        Or <Link to="/" data-testid="entry-guest" className="text-[#A1A1AA] hover:text-[#D4FF00] underline">browse as guest</Link>
      </p>

      {forgotOpen && <ForgotPasswordModal onClose={()=>setForgotOpen(false)} prefillEmail={email}/>}
    </div>
  );
}

function ForgotPasswordModal({ onClose, prefillEmail }) {
  const [step, setStep] = useState(1);   // 1=enter email, 2=enter token+new pw, 3=done
  const [email, setEmail] = useState(prefillEmail || "");
  const [token, setToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/forgot", { email });
      setMsg(data.message || "If the email exists, a reset code was sent.");
      setStep(2);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/reset", { token, new_password: newPw });
      setMsg(data.message || "Password updated");
      setStep(3);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-[#261534] w-full max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-[#D4FF00]"/>
            <div className="font-display font-black uppercase text-xl">Reset password</div>
          </div>
          <button onClick={onClose} data-testid="forgot-close-btn"><X size={18}/></button>
        </div>

        {step === 1 && (
          <form onSubmit={requestReset} className="space-y-3">
            <p className="text-sm text-[#A1A1AA]">Enter the email on your account. We'll send a one-time reset code.</p>
            <div className="relative">
              <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]"/>
              <input data-testid="forgot-email" type="email" required placeholder="Email" className="input-dark pl-10"
                value={email} onChange={e=>setEmail(e.target.value)} autoFocus/>
            </div>
            {err && <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2">{err}</div>}
            <button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="btn-primary w-full">
              {busy ? "Sending…" : "Send reset code"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitReset} className="space-y-3">
            <div className="bg-[#D4FF00]/10 border border-[#D4FF00]/30 rounded-lg px-3 py-2 text-xs text-[#D4FF00]">{msg}</div>
            <input data-testid="reset-token" required placeholder="Reset code" className="input-dark"
              value={token} onChange={e=>setToken(e.target.value)}/>
            <div className="relative">
              <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]"/>
              <input
                data-testid="reset-newpw"
                type={showNewPw ? "text" : "password"}
                required
                minLength={6}
                placeholder="New password (min 6 chars)"
                className="input-dark pl-10 pr-12"
                value={newPw}
                onChange={e=>setNewPw(e.target.value)}
              />
              <button type="button" onClick={()=>setShowNewPw(!showNewPw)}
                data-testid="reset-toggle-pw"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#D4FF00] p-1">
                {showNewPw ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
            {err && <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2">{err}</div>}
            <button type="submit" disabled={busy} data-testid="reset-submit-btn" className="btn-primary w-full">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-[#D4FF00] mx-auto flex items-center justify-center mb-3">
              <Check size={28} className="text-black"/>
            </div>
            <div className="font-display font-black uppercase text-2xl mb-2">All set!</div>
            <p className="text-sm text-[#A1A1AA] mb-4">Your password was updated. Sign in with the new password now.</p>
            <button onClick={onClose} data-testid="reset-done-btn" className="btn-primary w-full">Back to login</button>
          </div>
        )}
      </div>
    </div>
  );
}
